import React, { useState, useEffect } from 'react';
import { Globe, Plus, Edit, Trash2, Eye, Search, Filter, RefreshCw, X, Info, Power, Users, Building2, ChevronDown, ChevronRight, ArrowUp, ArrowDown, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { domainsAPI, companiesAPI, customersAPI, registrarsAPI, serversAPI } from '../lib/api';
import { getApiBaseUrl } from '../lib/apiClient';
import { useTranslation } from '../lib/i18n';

const Domains = () => {
  const { t } = useTranslation();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [registrars, setRegistrars] = useState([]);
  const [servers, setServers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', 'all'
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'customer', 'company'
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ current: 0, total: 0 });
  const bulkUpdateCancelledRef = React.useRef(false);
  const [whoisAlert, setWhoisAlert] = useState(null);
  const [whoisRegistrar, setWhoisRegistrar] = useState(null);
  const [formData, setFormData] = useState({
    domain_name: '',
    company_id: '',
    customer_id: '',
    registrar_id: '',
    server_id: '',
    status: 'active',
    registration_date: '',
    expiry_date: '',
    ns1: '',
    ns2: '',
    notes: '',
    domain_owner: 'customer'
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gruplandırma değiştiğinde tüm grupları aç
  useEffect(() => {
    if (groupBy === 'customer') {
      const groups = groupDomainsByCustomer();
      setExpandedGroups(new Set(groups.map(g => g.id)));
    } else if (groupBy === 'company') {
      const groups = groupDomainsByCompany();
      setExpandedGroups(new Set(groups.map(g => g.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [domainsData, customersData, companiesData, registrarsData, serversData] = await Promise.all([
        domainsAPI.getAll(),
        customersAPI.getAll(),
        companiesAPI.getAll(),
        registrarsAPI.getAll(),
        serversAPI.getAll()
      ]);

      
      setDomains(Array.isArray(domainsData) ? domainsData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setRegistrars(Array.isArray(registrarsData) ? registrarsData : []);
      setServers(Array.isArray(serversData) ? serversData : []);
    } catch (error) {
      console.error('Veri getirme hatası:', error);
      toast.error(t('domains.fetchError') + ': ' + (error.message || t('common.unknownError')));
      // Hata durumunda boş array'ler set et
      setDomains([]);
      setCustomers([]);
      setCompanies([]);
      setRegistrars([]);
      setServers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // UUID alanlar için boş string'leri null'a çevir
      const cleanUUIDField = (value) => {
        if (value === '' || value === null || value === undefined) {
          return null;
        }
        return String(value).trim() || null;
      };
      
      const createData = {
        ...formData,
        company_id: cleanUUIDField(formData.company_id),
        customer_id: cleanUUIDField(formData.customer_id),
        registrar_id: cleanUUIDField(formData.registrar_id),
        server_id: cleanUUIDField(formData.server_id)
      };
      
      await domainsAPI.create(createData);
      toast.success(t('domains.domainAdded'));
      setShowAddForm(false);
      setFormData({
        domain_name: '',
        customer_id: '',
        registrar_id: '',
        server_id: '',
        status: 'active',
        registration_date: '',
        expiry_date: '',
        notes: '',
        domain_owner: 'customer'
      });
      setWhoisRegistrar(null);
      fetchData();
    } catch (error) {
      console.error('Domain ekleme hatası:', error);
      toast.error(t('domains.addError') + ': ' + (error.message || t('common.unknownError')));
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingDomain(null);
    setFormData({
      domain_name: '',
      company_id: '',
      customer_id: '',
      registrar_id: '',
      server_id: '',
      status: 'active',
      registration_date: '',
      expiry_date: '',
      ns1: '',
      ns2: '',
      notes: '',
      domain_owner: 'customer'
    });
    setWhoisRegistrar(null);
  };

  const handleEdit = (domain) => {
    setEditingDomain(domain);
    
    // Tarih formatını HTML input type="date" için düzenle
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      // YYYY-MM-DD formatına çevir
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };
    
    setFormData({
      domain_name: domain.domain_name || '',
      company_id: domain.company_id ? String(domain.company_id) : '',
      customer_id: domain.customer_id ? String(domain.customer_id) : '',
      registrar_id: domain.registrar_id ? String(domain.registrar_id) : '',
      server_id: domain.server_id ? String(domain.server_id) : '',
      status: domain.status || 'active',
      registration_date: formatDateForInput(domain.registration_date),
      expiry_date: formatDateForInput(domain.expiry_date),
      ns1: domain.ns1 || '',
      ns2: domain.ns2 || '',
      notes: domain.notes || '',
      domain_owner: domain.domain_owner || 'customer'
    });
    
    setShowEditForm(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // UUID alanlar için boş string'leri null'a çevir
      const cleanUUIDField = (value) => {
        if (value === '' || value === null || value === undefined) {
          return null;
        }
        return String(value).trim() || null;
      };
      
      const updateData = {
        ...formData,
        company_id: cleanUUIDField(formData.company_id),
        customer_id: cleanUUIDField(formData.customer_id),
        registrar_id: cleanUUIDField(formData.registrar_id),
        server_id: cleanUUIDField(formData.server_id)
      };
      
      await domainsAPI.update(editingDomain.id, updateData);
      toast.success(t('domains.domainUpdated'));
      setShowEditForm(false);
      setEditingDomain(null);
      setFormData({
        domain_name: '',
        customer_id: '',
        registrar_id: '',
        server_id: '',
        status: 'active',
        registration_date: '',
        expiry_date: '',
        notes: '',
        domain_owner: 'customer'
      });
      fetchData();
    } catch (error) {
      console.error('Domain güncelleme hatası:', error);
      toast.error(t('domains.updateError'));
    }
  };

  const handleToggleStatus = async (domainId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      
      // Eğer pasife alıyorsak, onay diyalogu göster
      if (newStatus === 'inactive') {
        const domain = domains.find(d => d.id === domainId);
        if (!domain) return;
        
        // Aynı şirkete ait diğer aktif domainleri bul
        const relatedDomains = domains.filter(d =>
          d.id !== domainId &&
          d.status === 'active' &&
          d.company_id === domain.company_id &&
          d.company_id !== null
        );
        
        // Eğer ilişkili domain varsa, kullanıcıya sor
        if (relatedDomains.length > 0) {
          const userChoice = window.confirm(
            `${t('domains.deactivateConfirmMessage')}\n\n` +
            `Aynı şirkete (${domain.company_name}) ait ${relatedDomains.length} aktif domain daha var.\n\n` +
            `İlişkili domainleri de pasife almak için "OK" tuşuna basın.\n` +
            `Sadece bu domaini pasife almak için "İptal" tuşuna basın.`
          );
          
          if (userChoice) {
            // İlişkili domainleri de pasife al
            const domainsToDeactivate = [domainId, ...relatedDomains.map(d => d.id)];
            
            // Optimistic update - tüm domainleri pasife al
            setDomains(prevDomains =>
              prevDomains.map(domain =>
                domainsToDeactivate.includes(domain.id) ? { ...domain, status: 'inactive' } : domain
              )
            );
            
            // API çağrılarını yap
            try {
              await Promise.all(
                domainsToDeactivate.map(id => domainsAPI.update(id, { status: 'inactive' }))
              );
              
              toast.success(
                `${t('domains.domainDeactivated')}. ${t('domains.relatedDomainsDeactivated', { count: relatedDomains.length })}`
              );
            } catch (error) {
              console.error('İlişkili domainler pasife alınırken hata:', error);
              toast.error(t('domains.statusToggleError'));
              // Sadece hata olan domainleri geri al, API'den güncel veriyi çek
              const updatedDomains = await domainsAPI.getAll();
              setDomains(Array.isArray(updatedDomains) ? updatedDomains : []);
            }
            return;
          }
        }
      }
      
      // Normal toggle işlemi (aktife alma veya tek domain pasife alma)
      // Optimistic update - önce state'i güncelle
      setDomains(prevDomains =>
        prevDomains.map(domain =>
          domain.id === domainId ? { ...domain, status: newStatus } : domain
        )
      );
      
      // Sonra API'yi güncelle
      await domainsAPI.update(domainId, {
        status: newStatus
      });
      
      toast.success(newStatus === 'active' ? t('domains.domainActivated') : t('domains.domainDeactivated'));
    } catch (error) {
      console.error('Domain status toggle hatası:', error);
      toast.error(t('domains.statusToggleError'));
      // Hata durumunda sadece ilgili domaini geri yükle
      try {
        const updatedDomains = await domainsAPI.getAll();
        setDomains(Array.isArray(updatedDomains) ? updatedDomains : []);
      } catch (fetchError) {
        console.error('Domain verileri yeniden yüklenirken hata:', fetchError);
      }
    }
  };

  const handleDelete = async (domainId) => {
    if (window.confirm(t('domains.deleteConfirm'))) {
      try {
        await domainsAPI.delete(domainId);
        toast.success(t('domains.domainDeleted'));
        fetchData();
      } catch (error) {
        console.error('Domain silme hatası:', error);
        toast.error(t('domains.deleteError'));
      }
    }
  };

  const handleWhoisLookup = async () => {
    
    if (!formData.domain_name) {
      toast.error(t('domains.whoisEnterDomain'));
      return;
    }

    const domain = formData.domain_name.trim().toLowerCase();
    const maxRetries = 3;
    let lastError = null;

    toast.loading(t('domains.whoisQuerying'), { id: 'whois' });

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Backend WHOIS proxy endpoint - CORS sorununu çözer
        const apiUrl = `${getApiBaseUrl()}/whois?domain=${encodeURIComponent(domain)}`;
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`API yanıt hatası: ${response.status}`);
        }
        
        const whoisText = await response.text();
        
        // "No match" veya "Connection reset" kontrol et
        if (whoisText.includes('No match found') || whoisText.includes('Connection reset')) {
          if (attempt < maxRetries) {
            console.log(`⏳ WHOIS deneme ${attempt}/${maxRetries} başarısız, yeniden deneniyor...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
            continue;
          }
          toast.error(t('domains.whoisParseError'), { id: 'whois', duration: 5000 });
          return;
        }
        
        // WHOIS verilerini parse et ve forma doldur
        const parsedData = parseWhoisResponse(whoisText, domain);
        
        if (parsedData) {
          // WHOIS bilgilerini alert bölümünde göster
          setWhoisAlert({
            registration_date: parsedData.registration_date,
            expiry_date: parsedData.expiry_date,
            status: parsedData.status || 'active',
            ns1: parsedData.ns1,
            ns2: parsedData.ns2,
            registrar: parsedData.registrar
          });
          
          // Registrar bilgisini ayrı state'e kaydet
          if (parsedData.registrar) {
            setWhoisRegistrar(parsedData.registrar);
          }
          
          // WHOIS'ten gelen bilgileri forma doldur
          const currentDomainOwner = editingDomain?.domain_owner || formData.domain_owner || 'customer';
          const updatedFormData = {
            ...formData,
            registration_date: parsedData.registration_date || formData.registration_date || '',
            expiry_date: parsedData.expiry_date || formData.expiry_date || '',
            status: parsedData.status || formData.status || 'active',
            ns1: parsedData.ns1 ? parsedData.ns1.toLowerCase() : (formData.ns1 || ''),
            ns2: parsedData.ns2 ? parsedData.ns2.toLowerCase() : (formData.ns2 || ''),
            notes: formData.notes || '',
            domain_owner: currentDomainOwner
          };
          
          setFormData(updatedFormData);
          toast.success(t('domains.whoisSuccess'), { id: 'whois' });
          return;
        } else {
          if (attempt < maxRetries) {
            console.log(`⏳ WHOIS parse başarısız, deneme ${attempt}/${maxRetries}, yeniden deneniyor...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          toast.error(t('domains.whoisParseError'), { id: 'whois', duration: 5000 });
          return;
        }
      } catch (error) {
        lastError = error;
        console.error(`❌ WHOIS deneme ${attempt}/${maxRetries} hatası:`, error.message);
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }
      }
    }
    
    // Tüm denemeler başarısız
    console.error('❌ WHOIS sorgu hatası (tüm denemeler başarısız):', lastError);
    toast.error(`${t('domains.whoisFetchError')}: ${lastError?.message || t('common.unknownError')}`, {
      id: 'whois',
      duration: 5000
    });
  };

  // WHOIS yanıtını parse eden yardımcı fonksiyon
  // Softsynex WHOIS API - düz metin WHOIS çıktısı parse eder
  const parseWhoisResponse = (whoisText, domain) => {
    try {
      if (!whoisText || typeof whoisText !== 'string') {
        return null;
      }

      let registrationDate = null;
      let expiryDate = null;
      let registrar = null;
      let ns1 = null;
      let ns2 = null;
      let status = 'active';
      const nameServers = [];
      let inDomainServersSection = false;
      let inRegistrarSection = false;

      // Satır satır parse et
      const lines = whoisText.split('\n');
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine || trimmedLine.startsWith('>>>') || trimmedLine.startsWith('NOTICE:') || trimmedLine.startsWith('TERMS OF USE:')) {
          continue;
        }

        // .tr domainleri için bölüm tespiti
        if (trimmedLine === '** Domain Servers:') {
          inDomainServersSection = true;
          inRegistrarSection = false;
          continue;
        }
        if (trimmedLine === '** Registrar:') {
          inRegistrarSection = true;
          inDomainServersSection = false;
          continue;
        }
        if (trimmedLine.startsWith('** ') && trimmedLine !== '** Domain Servers:' && trimmedLine !== '** Registrar:') {
          inDomainServersSection = false;
          inRegistrarSection = false;
        }

        // .tr domainleri için Domain Servers bölümündeki NS'leri al
        if (inDomainServersSection) {
          // NS satırları genellikle sadece domain ismi içerir
          if (trimmedLine && !trimmedLine.includes(':') && trimmedLine.includes('.')) {
            const nsValue = trimmedLine.toUpperCase();
            if (!nameServers.includes(nsValue)) {
              nameServers.push(nsValue);
            }
          }
          continue;
        }

        // Key: Value formatını parse et
        const colonIndex = trimmedLine.indexOf(':');
        if (colonIndex === -1) continue;
        
        // .tr domainleri için özel key formatı (Created on..............: gibi)
        let key = trimmedLine.substring(0, colonIndex).trim().toLowerCase();
        // Nokta karakterlerini temizle
        key = key.replace(/\.+$/, '').trim();
        
        const value = trimmedLine.substring(colonIndex + 1).trim();
        
        if (!value) continue;

        // Creation Date - farklı varyasyonlar (standart WHOIS + .tr formatı)
        if (key === 'creation date' || key === 'created date' || key === 'created' ||
            key === 'registration date' || key === 'domain registration date' ||
            key === 'created on') {
          registrationDate = value;
        }
        
        // Expiry Date - farklı varyasyonlar (standart WHOIS + .tr formatı)
        if (key === 'registry expiry date' || key === 'registrar registration expiration date' ||
            key === 'expiration date' || key === 'expiry date' || key === 'expires' ||
            key === 'domain expiration date' || key === 'paid-till' ||
            key === 'expires on') {
          expiryDate = value;
        }
        
        // Registrar - standart format
        if (key === 'registrar' && !registrar) {
          registrar = value;
        }
        
        // Registrar - .tr formatı (Organization Name in Registrar section)
        if (inRegistrarSection && key === 'organization name' && !registrar) {
          registrar = value;
        }
        
        // Name Servers - standart format
        if (key === 'name server' || key === 'nserver' || key === 'nameserver') {
          const nsValue = value.split(' ')[0].toUpperCase(); // IP adresini kaldır
          if (!nameServers.includes(nsValue)) {
            nameServers.push(nsValue);
          }
        }
        
        // Status - domain durumu
        if (key === 'domain status' || key === 'status') {
          const statusLower = value.toLowerCase();
          if (statusLower.includes('expired') || statusLower.includes('redemption') ||
              statusLower.includes('pending delete')) {
            status = 'inactive';
          }
        }
      }

      // Name server'ları ata
      if (nameServers.length > 0) {
        ns1 = nameServers[0];
        ns2 = nameServers[1] || null;
      }

      // Eğer hiçbir veri bulunamadıysa null döndür
      if (!registrationDate && !expiryDate && !registrar && !ns1) {
        console.log('⚠️ WHOIS parse: Hiçbir veri bulunamadı');
        return null;
      }
      
      const parsed = {
        registration_date: registrationDate ? formatDate(registrationDate) : null,
        expiry_date: expiryDate ? formatDate(expiryDate) : null,
        status: status || 'active',
        ns1: ns1,
        ns2: ns2,
        registrar: registrar
      };
      
      return parsed;
      
    } catch (error) {
      console.error('❌ WHOIS parse hatası:', error);
      return null;
    }
  };

  // Tarih formatını düzenle (YYYY-MM-DD)
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      // .tr formatı için: "2020-Nov-02." veya "2020-Nov-02" (YYYY-Mon-DD)
      let processedDate = dateString;
      
      // Eğer tarih "YYYY-Mon-DD" formatındaysa (örn: "2020-Nov-02"), çevir
      const trMatch = processedDate.match(/^(\d{4})-([A-Za-z]{3})-(\d{2})\.?$/);
      if (trMatch) {
        const [, year, monthStr, day] = trMatch;
        const months = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
                        Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
        const month = months[monthStr] || '01';
        return `${year}-${month}-${day}`;
      }
      
      const date = new Date(processedDate);
      if (isNaN(date.getTime())) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return null;
    }
  };

  const handleBulkWhoisUpdate = async () => {
    if (filteredDomains.length === 0) {
      toast.error(t('domains.bulkUpdateNoDomains'));
      return;
    }

    bulkUpdateCancelledRef.current = false;
    setBulkUpdating(true);
    
    // Sadece aktif domainleri ve .at dışındaki domainleri filtrele
    const domainsToUpdate = filteredDomains.filter(domain => {
      const domainName = domain.domain_name?.toLowerCase() || '';
      const isActive = domain.status === 'active';
      const isNotAtDomain = !domainName.endsWith('.at');
      return isActive && isNotAtDomain;
    });
    
    const inactiveCount = filteredDomains.filter(d => d.status !== 'active').length;
    
    const atDomainsCount = filteredDomains.filter(d => d.domain_name?.toLowerCase().endsWith('.at')).length;
    const skippedMessages = [];
    
    if (inactiveCount > 0) {
      skippedMessages.push(`${inactiveCount} ${t('domains.bulkUpdateSkippedInactive')}`);
    }
    
    if (atDomainsCount > 0) {
      skippedMessages.push(`${atDomainsCount} ${t('domains.bulkUpdateSkippedDomains')}`);
    }
    
    if (skippedMessages.length > 0) {
      toast(skippedMessages.join(', '), {
        icon: 'ℹ️',
        duration: 4000
      });
    }
    
    if (domainsToUpdate.length === 0) {
      toast.error(t('domains.bulkUpdateNoDomains'));
      setBulkUpdating(false);
      return;
    }
    
    setUpdateProgress({ current: 0, total: domainsToUpdate.length });
    
    let successCount = 0;
    let skippedCount = 0; // Null veri nedeniyle atlananlar
    const failedDomains = []; // Başarısız olan domainler

    // Retry helper function with exponential backoff
    const fetchWithRetry = async (domainName, maxRetries = 3) => {
      let lastError = null;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        // İptal kontrolü
        if (bulkUpdateCancelledRef.current) {
          throw new Error('Cancelled');
        }
        
        try {
          const response = await fetch(`${getApiBaseUrl()}/whois?domain=${encodeURIComponent(domainName)}`);
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }
          
          const whoisText = await response.text();
          return whoisText;
          
        } catch (error) {
          lastError = error;
          if (error.message === 'Cancelled') throw error;
          
          console.warn(`Attempt ${attempt}/${maxRetries} failed for ${domainName}:`, error.message);
          
          if (attempt < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt - 1) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      throw lastError;
    };

    try {
      for (let i = 0; i < domainsToUpdate.length; i++) {
        // İptal kontrolü
        if (bulkUpdateCancelledRef.current) {
          toast('İptal edildi', { icon: 'ℹ️', duration: 3000 });
          break;
        }
        
        const domain = domainsToUpdate[i];
        setUpdateProgress({ current: i + 1, total: domainsToUpdate.length });
        
        // İlk domain değilse 3 saniye bekle (her istek öncesi)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
        
        try {
          // WHOIS sorgusu with retry logic
          const whoisText = await fetchWithRetry(domain.domain_name);
          
          // İptal kontrolü
          if (bulkUpdateCancelledRef.current) {
            toast('İptal edildi', { icon: 'ℹ️', duration: 3000 });
            break;
          }
          
          // WHOIS verilerini parse et
          const parsedData = parseWhoisResponse(whoisText, domain.domain_name);
          
          // Eğer parse edilen veri null ise veya hiçbir geçerli veri yoksa atla
          if (!parsedData) {
            skippedCount++;
            failedDomains.push({
              domain: domain.domain_name,
              reason: 'WHOIS verisi parse edilemedi veya null'
            });
            continue;
          }
          
          // Null verileri kontrol et - eğer tüm önemli alanlar null ise atla
          const hasValidData = parsedData.registration_date ||
                               parsedData.expiry_date ||
                               parsedData.ns1 ||
                               parsedData.ns2 ||
                               parsedData.registrar;
          
          if (!hasValidData) {
            skippedCount++;
            failedDomains.push({
              domain: domain.domain_name,
              reason: 'WHOIS verisi null veya boş'
            });
            continue;
          }
          
          // Sadece null olmayan verileri güncelle (mevcut değerleri koru)
          const updateData = {
            domain_name: domain.domain_name,
            company_id: domain.company_id,
            customer_id: domain.customer_id,
            registrar_id: domain.registrar_id,
            server_id: domain.server_id,
            notes: domain.notes || '',
            domain_owner: domain.domain_owner || 'customer'
          };
          
          // Değişiklikleri takip et
          const changes = [];
          
          // Sadece null olmayan verileri ekle
          if (parsedData.registration_date) {
            if (parsedData.registration_date !== domain.registration_date) {
              changes.push(`registration_date: "${domain.registration_date || 'null'}" → "${parsedData.registration_date}"`);
            }
            updateData.registration_date = parsedData.registration_date;
          } else if (domain.registration_date) {
            updateData.registration_date = domain.registration_date;
          }
          
          if (parsedData.expiry_date) {
            if (parsedData.expiry_date !== domain.expiry_date) {
              changes.push(`expiry_date: "${domain.expiry_date || 'null'}" → "${parsedData.expiry_date}"`);
            }
            updateData.expiry_date = parsedData.expiry_date;
          } else if (domain.expiry_date) {
            updateData.expiry_date = domain.expiry_date;
          }
          
          if (parsedData.status) {
            if (parsedData.status !== domain.status) {
              changes.push(`status: "${domain.status || 'null'}" → "${parsedData.status}"`);
            }
            updateData.status = parsedData.status;
          } else if (domain.status) {
            updateData.status = domain.status;
          }
          
          if (parsedData.ns1) {
            const newNs1 = parsedData.ns1.toLowerCase();
            if (newNs1 !== domain.ns1) {
              changes.push(`ns1: "${domain.ns1 || 'null'}" → "${newNs1}"`);
            }
            updateData.ns1 = newNs1;
          } else if (domain.ns1) {
            updateData.ns1 = domain.ns1;
          }
          
          if (parsedData.ns2) {
            const newNs2 = parsedData.ns2.toLowerCase();
            if (newNs2 !== domain.ns2) {
              changes.push(`ns2: "${domain.ns2 || 'null'}" → "${newNs2}"`);
            }
            updateData.ns2 = newNs2;
          } else if (domain.ns2) {
            updateData.ns2 = domain.ns2;
          }
          
          // Console'a değişiklikleri yazdır
          if (changes.length > 0) {
            console.log(`✅ ${domain.domain_name} güncellendi:`);
            changes.forEach(change => console.log(`   ${change}`));
          } else {
            console.log(`ℹ️ ${domain.domain_name}: Her şey aynı, güncelleme gerekmiyor`);
          }
          
          // Domain'i güncelle
          await domainsAPI.update(domain.id, updateData);
          
          successCount++;
          
        } catch (error) {
          if (error.message === 'Cancelled') {
            break;
          }
          console.error(`❌ ${domain.domain_name} güncellenirken hata:`, error);
          failedDomains.push({
            domain: domain.domain_name,
            reason: error.message || 'Bilinmeyen hata'
          });
        }
      }
      
      // Sonuçları göster
      const messages = [];
      
      if (successCount > 0) {
        messages.push(`✅ ${successCount} ${t('domains.bulkUpdateSuccess')}`);
        toast.success(`${successCount} ${t('domains.bulkUpdateSuccess')}`, { duration: 5000 });
      }
      
      if (skippedCount > 0) {
        messages.push(`⚠️ ${skippedCount} ${t('domains.bulkUpdateSkipped')}`);
        toast(`${skippedCount} ${t('domains.bulkUpdateSkipped')}`, {
          icon: '⚠️',
          duration: 5000
        });
      }
      
      if (failedDomains.length > 0) {
        // Başarısız domainleri uyarı olarak göster
        toast.error(
          `${failedDomains.length} domain güncellenemedi:\n${failedDomains.slice(0, 5).map(f => f.domain).join(', ')}${failedDomains.length > 5 ? '...' : ''}`,
          {
            duration: 8000
          }
        );
        
        // Tüm başarısız domainleri console'a yazdır
        console.table(failedDomains);
      }
      
      // Domain listesini yenile
      if (successCount > 0) {
        await fetchData();
      }
      
    } catch (error) {
      console.error('❌ Toplu güncelleme hatası:', error);
      toast.error(`${t('domains.bulkUpdateError')}: ${error.message}`);
    } finally {
      setBulkUpdating(false);
      setUpdateProgress({ current: 0, total: 0 });
      bulkUpdateCancelledRef.current = false;
    }
  };

  const handleCancelBulkUpdate = () => {
    bulkUpdateCancelledRef.current = true;
    toast('İptal ediliyor...', { icon: '⏳', duration: 2000 });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ArrowUp size={14} className="inline ml-1" /> : <ArrowDown size={14} className="inline ml-1" />;
  };

  // XLSX Export fonksiyonu
  const handleExport = () => {
    const dataToExport = filteredDomains.map(domain => ({
      [t('domains.domainName')]: domain.domain_name || '',
      [t('domains.company')]: domain.company_name || '',
      [t('customers.name')]: domain.customer_name || '',
      [t('domains.registrar')]: domain.registrar_name || '',
      [t('domains.server')]: domain.server_name || '',
      [t('domains.status')]: domain.status === 'active' ? t('domains.statusActive') : t('domains.statusInactive'),
      [t('domains.registrationDate')]: domain.registration_date ? new Date(domain.registration_date).toLocaleDateString('tr-TR') : '',
      [t('domains.expiryDate')]: domain.expiry_date ? new Date(domain.expiry_date).toLocaleDateString('tr-TR') : '',
      'NS1': domain.ns1 || '',
      'NS2': domain.ns2 || '',
      [t('domains.domainOwner')]: domain.domain_owner === 'ours' ? t('domains.domainOwnerOurs') : t('domains.domainOwnerCustomer'),
      [t('domains.notes')]: domain.notes || ''
    }));

    // XLSX oluştur
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Domains');
    
    // Dosyayı indir
    XLSX.writeFile(wb, `domains_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast.success(t('domains.exportSuccess'));
  };

  const filteredDomains = domains.filter(domain => {
    // Status filter
    if (statusFilter === 'active' && domain.status !== 'active') return false;
    if (statusFilter === 'inactive' && domain.status !== 'inactive') return false;
    
    // Search filter
    const searchMatch =
      domain.domain_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.registrar_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return searchMatch;
  }).sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue, bValue;
    
    switch (sortColumn) {
      case 'domain_name':
        aValue = a.domain_name?.toLowerCase() || '';
        bValue = b.domain_name?.toLowerCase() || '';
        break;
      case 'customer_name':
      case 'company_name':
        aValue = a.company_name?.toLowerCase() || '';
        bValue = b.company_name?.toLowerCase() || '';
        break;
      case 'registrar_name':
        aValue = a.registrar_name?.toLowerCase() || '';
        bValue = b.registrar_name?.toLowerCase() || '';
        break;
      case 'server_name':
        aValue = a.server_name?.toLowerCase() || '';
        bValue = b.server_name?.toLowerCase() || '';
        break;
      case 'status':
        aValue = a.status?.toLowerCase() || '';
        bValue = b.status?.toLowerCase() || '';
        break;
      case 'expiry_date':
        aValue = a.expiry_date ? new Date(a.expiry_date).getTime() : 0;
        bValue = b.expiry_date ? new Date(b.expiry_date).getTime() : 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Gruplandırma fonksiyonları
  const groupDomainsByCustomer = () => {
    const grouped = {};
    filteredDomains.forEach(domain => {
      const customerId = domain.customer_id;
      const customerName = domain.customer_name || t('domains.unassignedCustomer');
      const key = customerId ? `${customerId}-${customerName}` : 'no-customer';
      
      if (!grouped[key]) {
        grouped[key] = {
          id: customerId || 'no-customer',
          name: customerName,
          domains: []
        };
      }
      grouped[key].domains.push(domain);
    });
    return Object.values(grouped);
  };

  const groupDomainsByCompany = () => {
    const grouped = {};
    filteredDomains.forEach(domain => {
      // Domain'in doğrudan company_id'sini kullan, yoksa customer üzerinden bul
      let companyId = domain.company_id;
      let companyName = domain.company_name;
      
      // Eğer domain'de company_id yoksa, customer üzerinden bul
      if (!companyId && domain.customer_id) {
        const customer = customers.find(c => c.id === domain.customer_id);
        companyId = customer?.company_id;
        if (companyId) {
          const company = companies.find(comp => comp.id === companyId);
          companyName = company?.name;
        }
      }
      
      const finalCompanyName = companyName || t('domains.unassignedCompany');
      const key = companyId ? `${companyId}-${finalCompanyName}` : 'no-company';
      
      if (!grouped[key]) {
        grouped[key] = {
          id: companyId || 'no-company',
          name: finalCompanyName,
          domains: []
        };
      }
      grouped[key].domains.push(domain);
    });
    return Object.values(grouped);
  };

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Kalan gün sayısını hesapla
  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Domain Row Component
  const DomainRow = ({ domain, showCustomer = false }) => {
    const daysRemaining = getDaysRemaining(domain.expiry_date);
    const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30 && daysRemaining >= 0;
    
    return (
      <tr className="hover:bg-gray-50 transition-colors duration-150">
        <td className="px-4 py-3">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center mr-2 flex-shrink-0">
              <Globe className="h-4 w-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{domain.domain_name}</div>
            </div>
          </div>
        </td>
        {showCustomer && (
          <td className="px-4 py-3">
            {domain.company_name && (
              <div className="flex items-center gap-1">
                <Building2 size={14} className="text-gray-400" />
                <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">{domain.company_name}</div>
              </div>
            )}
            {!domain.company_name && (
              <div className="text-sm text-gray-400 truncate max-w-[120px]">-</div>
            )}
          </td>
        )}
        <td className="px-4 py-3">
          <div className="flex flex-col space-y-1">
            <div className="text-sm text-gray-900 truncate max-w-[120px]">{domain.registrar_name || 'N/A'}</div>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${
              domain.domain_owner === 'ours' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
            }`}>
              {domain.domain_owner === 'ours' ? t('domains.domainOwnerOurs') : t('domains.domainOwnerCustomer')}
            </span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm text-gray-900 truncate max-w-[120px]">{domain.server_name || 'N/A'}</div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              domain.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {domain.status === 'active' ? t('domains.statusActive') : t('domains.statusInactive')}
            </span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggleStatus(domain.id, domain.status);
              }}
              className={`p-1 rounded transition-colors ${
                domain.status === 'active'
                  ? 'text-green-600 hover:bg-green-50'
                  : 'text-gray-400 hover:bg-gray-50'
              }`}
              title={domain.status === 'active' ? t('domains.deactivate') : t('domains.activate')}
            >
              <Power className="h-4 w-4" />
            </button>
          </div>
        </td>
        <td className={`px-4 py-3 text-sm whitespace-nowrap ${isExpiringSoon ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
          {domain.expiry_date ? (
            <div className="flex flex-col">
              <div>{new Date(domain.expiry_date).toLocaleDateString('tr-TR')}</div>
              {daysRemaining !== null && (
                <div className={`text-xs mt-1 ${isExpiringSoon ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                  {daysRemaining < 0
                    ? `${t('domains.expired')} (${Math.abs(daysRemaining)} ${t('domains.expiredDaysAgo')})`
                    : daysRemaining === 0
                    ? t('domains.todayLastDay')
                    : `${daysRemaining} ${t('domains.daysLeft')}`
                  }
                </div>
              )}
            </div>
          ) : (
            'N/A'
          )}
        </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        <div className="flex flex-col space-y-1 max-w-[200px]">
          {domain.ns1 && (
            <div className="text-xs font-mono truncate" title={domain.ns1}>{domain.ns1}</div>
          )}
          {domain.ns2 && (
            <div className="text-xs font-mono truncate" title={domain.ns2}>{domain.ns2}</div>
          )}
          {!domain.ns1 && !domain.ns2 && (
            <div className="text-xs text-gray-400">N/A</div>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm font-medium">
        <div className="flex space-x-2">
          <button
            className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
            title={t('domains.viewDetails')}
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEdit(domain)}
            className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
            title={t('domains.edit')}
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(domain.id)}
            className="text-red-600 hover:text-red-900 transition-colors duration-200"
            title={t('domains.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* WHOIS Uyarı Bölümü */}
      {whoisAlert && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 p-4 rounded-lg shadow-md">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <Info className="text-blue-500 dark:text-blue-400 mt-1 mr-3" size={20} />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">{t('domains.whoisTitle')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
                  {whoisAlert.registration_date && (
                    <div>
                      <span className="font-medium">📅 {t('domains.whoisRegistrationDate')}:</span> {whoisAlert.registration_date}
                    </div>
                  )}
                  {whoisAlert.expiry_date && (
                    <div>
                      <span className="font-medium">📅 {t('domains.whoisExpiryDate')}:</span> {whoisAlert.expiry_date}
                    </div>
                  )}
                  {whoisAlert.status && (
                    <div>
                      <span className="font-medium">✅ {t('domains.whoisStatus')}:</span> {whoisAlert.status}
                    </div>
                  )}
                  {whoisAlert.ns1 && (
                    <div>
                      <span className="font-medium">🌐 {t('domains.whoisNs1')}:</span> {whoisAlert.ns1}
                    </div>
                  )}
                  {whoisAlert.ns2 && (
                    <div>
                      <span className="font-medium">🌐 {t('domains.whoisNs2')}:</span> {whoisAlert.ns2}
                    </div>
                  )}
                  {whoisAlert.registrar && (
                    <div className="md:col-span-2">
                      <span className="font-medium">🏢 {t('domains.whoisRegistrar')}:</span> {whoisAlert.registrar}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setWhoisAlert(null)}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label={t('domains.whoisClose')}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('domains.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('domains.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Download className="h-5 w-5" />
              <span className="font-medium">{t('domains.exportButton')}</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">{t('domains.addNew')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder={t('domains.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200">
              <Filter className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-gray-700 dark:text-gray-300">{t('common.filter')}</span>
            </button>
            <button className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200">
              {filteredDomains.length} {t('domains.resultsFound')}
            </button>
          </div>
        </div>
      </div>

      {/* Domain Ekleme Formu */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('domains.formTitle')}</h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.domainName')} *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    name="domain_name"
                    value={formData.domain_name}
                    onChange={handleInputChange}
                    required
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('domains.domainNamePlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleWhoisLookup();
                    }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    title={t('domains.whoisButton')}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="hidden sm:inline">{t('domains.whoisButton')}</span>
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.company')} *
                </label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('domains.selectCompany')}</option>
                  {loading ? (
                    <option value="" disabled>{t('domains.companyLoading')}</option>
                  ) : Array.isArray(companies) && companies.length > 0 ? (
                    companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>{t('domains.companyNotFound')}</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.registrar')} *
                </label>
                <select
                  name="registrar_id"
                  value={formData.registrar_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('domains.selectRegistrar')}</option>
                  {Array.isArray(registrars) && registrars.map(registrar => (
                    <option key={registrar.id} value={registrar.id}>
                      {registrar.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.server')}
                </label>
                <select
                  name="server_id"
                  value={formData.server_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('domains.selectServer')}</option>
                  {Array.isArray(servers) && servers.map(server => (
                    <option key={server.id} value={server.id}>
                      {server.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.status')}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">{t('domains.statusActive')}</option>
                  <option value="inactive">{t('domains.statusInactive')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.registrationDate')}
                </label>
                <input
                  type="date"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.expiryDate')}
                </label>
                <input
                  type="date"
                  name="expiry_date"
                  value={formData.expiry_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.domainOwner')}
                </label>
                <select
                  name="domain_owner"
                  value={formData.domain_owner}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="customer">{t('domains.domainOwnerCustomer')}</option>
                  <option value="ours">{t('domains.domainOwnerOurs')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.formRegistrar')}
                </label>
                <input
                  type="text"
                  value={whoisRegistrar || ''}
                  disabled
                  readOnly
                  placeholder={t('domains.whoisRegistrarPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.ns1')}
                </label>
                <input
                  type="text"
                  name="ns1"
                  value={formData.ns1}
                  onChange={handleInputChange}
                  placeholder={t('domains.ns1Placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.ns2')}
                </label>
                <input
                  type="text"
                  name="ns2"
                  value={formData.ns2}
                  onChange={handleInputChange}
                  placeholder={t('domains.ns2Placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('domains.notes')}
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('domains.notesPlaceholder')}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('domains.cancelButton')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('domains.addButton')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Domain Düzenleme Modalı */}
      {showEditForm && editingDomain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCancel}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('domains.formEditTitle')}</h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleUpdate} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.domainName')} *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    name="domain_name"
                    value={formData.domain_name}
                    onChange={handleInputChange}
                    required
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('domains.domainNamePlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleWhoisLookup();
                    }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    title={t('domains.whoisButton')}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="hidden sm:inline">{t('domains.whoisButton')}</span>
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.company')} *
                </label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('domains.selectCompany')}</option>
                  {loading ? (
                    <option value="" disabled>{t('domains.companyLoading')}</option>
                  ) : Array.isArray(companies) && companies.length > 0 ? (
                    companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>{t('domains.companyNotFound')}</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.registrar')} *
                </label>
                <select
                  name="registrar_id"
                  value={formData.registrar_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('domains.selectRegistrar')}</option>
                  {Array.isArray(registrars) && registrars.map(registrar => (
                    <option key={registrar.id} value={registrar.id}>
                      {registrar.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.server')}
                </label>
                <select
                  name="server_id"
                  value={formData.server_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('domains.selectServer')}</option>
                  {Array.isArray(servers) && servers.map(server => (
                    <option key={server.id} value={server.id}>
                      {server.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.status')}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">{t('domains.statusActive')}</option>
                  <option value="inactive">{t('domains.statusInactive')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.domainOwner')}
                </label>
                <select
                  name="domain_owner"
                  value={formData.domain_owner}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="customer">{t('domains.domainOwnerCustomer')}</option>
                  <option value="ours">{t('domains.domainOwnerOurs')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.registrationDate')}
                </label>
                <input
                  type="date"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.expiryDate')}
                </label>
                <input
                  type="date"
                  name="expiry_date"
                  value={formData.expiry_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.formRegistrar')}
                </label>
                <input
                  type="text"
                  value={whoisRegistrar || ''}
                  disabled
                  readOnly
                  placeholder={t('domains.whoisRegistrarPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.ns1')}
                </label>
                <input
                  type="text"
                  name="ns1"
                  value={formData.ns1}
                  onChange={handleInputChange}
                  placeholder={t('domains.ns1Placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('domains.ns2')}
                </label>
                <input
                  type="text"
                  name="ns2"
                  value={formData.ns2}
                  onChange={handleInputChange}
                  placeholder={t('domains.ns2Placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('domains.notes')}
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('domains.notesPlaceholder')}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('domains.cancelButton')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('domains.updateButton')}
              </button>
            </div>
          </form>
        </div>
        </div>
      )}

      {/* Domain Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('domains.domainList')}</h3>
          </div>
          
          {/* Gruplandırma Sekmeleri */}
          <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setGroupBy('none')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                groupBy === 'none'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t('domains.tabAll')}
            </button>
            <button
              onClick={() => setGroupBy('customer')}
              className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                groupBy === 'customer'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Users className="h-4 w-4" />
              {t('domains.tabByCustomer')}
            </button>
            <button
              onClick={() => setGroupBy('company')}
              className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                groupBy === 'company'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Building2 className="h-4 w-4" />
              {t('domains.tabByCompany')}
            </button>
          </div>
          
          {/* Status Filter Buttons */}
          <div className="flex items-center gap-2 mt-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('domains.status')}:</span>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('domains.statusActive')}
            </button>
            <button
              onClick={() => setStatusFilter('inactive')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === 'inactive'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('domains.statusInactive')}
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === 'all'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('domains.filterAll')}
            </button>
          </div>
          
          <div className="flex items-center gap-3 mt-4">
            {bulkUpdating && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400"></div>
                <span>{t('domains.updating')}: {updateProgress.current}/{updateProgress.total}</span>
              </div>
            )}
            <button
              onClick={handleBulkWhoisUpdate}
              disabled={bulkUpdating || filteredDomains.length === 0}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              {bulkUpdating ? <RefreshCw size={16} className="animate-spin" /> : <Globe size={16} />}
              {bulkUpdating ? t('domains.updating') : t('domains.bulkWhoisUpdate')}
            </button>
            {bulkUpdating && (
              <button
                onClick={handleCancelBulkUpdate}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
              >
                <X size={16} />
                {t('common.cancel')}
              </button>
            )}
          </div>
        </div>
        {bulkUpdating && (
          <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(updateProgress.current / updateProgress.total) * 100}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {t('domains.domainsUpdated', { current: updateProgress.current, total: updateProgress.total })}
            </p>
          </div>
        )}
        {/* Gruplandırılmış Görünüm */}
        {groupBy === 'customer' && (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {groupDomainsByCustomer().map((group) => (
              <div key={group.id} className="border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {expandedGroups.has(group.id) ? (
                      <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    )}
                    <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">{group.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{group.domains.length} {t('domains.domains')}</div>
                    </div>
                  </div>
                </button>
                {expandedGroups.has(group.id) && (
                  <div className="px-6 pb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-gray-100 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableDomain')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('domains.tableRegistrar')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('domains.tableServer')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('domains.tableStatus')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('domains.tableExpiry')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('domains.tableNs')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                          {group.domains.map((domain) => (
                            <DomainRow key={domain.id} domain={domain} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {groupBy === 'company' && (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {groupDomainsByCompany().map((group) => (
              <div key={group.id} className="border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {expandedGroups.has(group.id) ? (
                      <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    )}
                    <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">{group.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{group.domains.length} {t('domains.domains')}</div>
                    </div>
                  </div>
                </button>
                {expandedGroups.has(group.id) && (
                  <div className="px-6 pb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-gray-100 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableDomain')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableCompany')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableRegistrar')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableServer')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableStatus')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableExpiry')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('domains.tableNs')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                          {group.domains.map((domain) => (
                            <DomainRow key={domain.id} domain={domain} showCustomer={true} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Normal Tablo Görünümü */}
        {groupBy === 'none' && (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-100 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                    onClick={() => handleSort('domain_name')}
                  >
                    {t('domains.tableDomain')} {getSortIcon('domain_name')}
                </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                    onClick={() => handleSort('company_name')}
                  >
                  {t('domains.tableCompany')} {getSortIcon('company_name')}
                </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                    onClick={() => handleSort('registrar_name')}
                  >
                  {t('domains.tableRegistrar')} {getSortIcon('registrar_name')}
                </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                    onClick={() => handleSort('server_name')}
                  >
                  {t('domains.tableServer')} {getSortIcon('server_name')}
                </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                    onClick={() => handleSort('status')}
                  >
                  {t('domains.tableStatus')} {getSortIcon('status')}
                </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                    onClick={() => handleSort('expiry_date')}
                  >
                    {t('domains.tableExpiry')} {getSortIcon('expiry_date')}
                </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('domains.tableNs')}
                </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {filteredDomains.map((domain) => (
                <tr key={domain.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center mr-2 flex-shrink-0">
                        <Globe className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{domain.domain_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {domain.company_name && (
                      <div className="flex items-center gap-1">
                        <Building2 size={14} className="text-gray-400 dark:text-gray-500" />
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[120px]">{domain.company_name}</div>
                      </div>
                    )}
                    {!domain.company_name && (
                      <div className="text-sm text-gray-400 dark:text-gray-500 truncate max-w-[120px]">-</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col space-y-1">
                      <div className="text-sm text-gray-900 dark:text-white truncate max-w-[120px]">{domain.registrar_name || 'N/A'}</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${
                        domain.domain_owner === 'ours' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {domain.domain_owner === 'ours' ? t('domains.domainOwnerOurs') : t('domains.domainOwnerCustomer')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 truncate max-w-[120px]">{domain.server_name || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        domain.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {domain.status === 'active' ? t('domains.statusActive') : t('domains.statusInactive')}
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleStatus(domain.id, domain.status);
                        }}
                        className={`p-1 rounded transition-colors ${
                          domain.status === 'active'
                            ? 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20'
                            : 'text-gray-400 hover:bg-gray-50 dark:text-gray-500 dark:hover:bg-gray-700'
                        }`}
                        title={domain.status === 'active' ? t('domains.deactivate') : t('domains.activate')}
                      >
                        <Power className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${
                    (() => {
                      const daysRemaining = getDaysRemaining(domain.expiry_date);
                      const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30 && daysRemaining >= 0;
                      return isExpiringSoon ? 'text-red-600 font-semibold' : 'text-gray-500';
                    })()
                  }`}>
                    {domain.expiry_date ? (
                      (() => {
                        const daysRemaining = getDaysRemaining(domain.expiry_date);
                        const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30 && daysRemaining >= 0;
                        return (
                          <div className="flex flex-col">
                            <div>{new Date(domain.expiry_date).toLocaleDateString('tr-TR')}</div>
                            {daysRemaining !== null && (
                              <div className={`text-xs mt-1 ${isExpiringSoon ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                {daysRemaining < 0 
                                  ? `${t('domains.expired')} (${Math.abs(daysRemaining)} ${t('domains.expiredDaysAgo')})`
                                  : daysRemaining === 0
                                  ? t('domains.todayLastDay')
                                  : `${daysRemaining} ${t('domains.daysLeft')}`
                                }
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div className="flex flex-col space-y-1 max-w-[200px]">
                      {domain.ns1 && (
                        <div className="text-xs font-mono truncate" title={domain.ns1}>{domain.ns1}</div>
                      )}
                      {domain.ns2 && (
                        <div className="text-xs font-mono truncate" title={domain.ns2}>{domain.ns2}</div>
                      )}
                      {!domain.ns1 && !domain.ns2 && (
                        <div className="text-xs text-gray-400">N/A</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                        title={t('domains.viewDetails')}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleEdit(domain)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                        title={t('domains.edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(domain.id)}
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        title={t('domains.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>


      {filteredDomains.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
          <Globe className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchTerm ? t('domains.noSearchResults') : t('domains.noDomains')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? t('domains.tryDifferentSearch') : t('domains.addFirstDomain')}
          </p>
        </div>
      )}
    </div>
  );
};

export default Domains;
